import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { MapPin, Mail, Phone, Facebook, Youtube, Instagram } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import PageMeta from '../components/PageMeta';

const Contact = () => {
  const { language } = useLanguage();

  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61564415150178', icon: Facebook },
    { name: 'YouTube', url: 'https://www.youtube.com/@MekaneHiwetEyesusDallas', icon: Youtube },
    { name: 'Instagram', url: 'https://instagram.com/mhe_orthodox_dallas', icon: Instagram },
    { name: 'TikTok', url: 'https://tiktok.com/@mhe_tewahdo_dallas', icon: FaTiktok },
    { name: 'WhatsApp', url: 'https://chat.whatsapp.com/your-group-link', icon: 'whatsapp' }
  ];

  return (


    <>


      <PageMeta 


        title="Contact Us"


        path="/contact"


        description="Get in touch with Mekane Hiwet Eyesus Dallas. Visit us, call, or email for service times, events, and spiritual guidance."


      />
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'contact.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {language === 'en' ? 'We would love to hear from you' : 'ሓሳባትኩም ንምስማዓ ኩሉ ሳዓ ዱሉዋት ኢና'}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Address */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900 mb-4">
                <MapPin className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t(language, 'contact.address')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                1450 E Campbell Rd<br />
                Richardson, TX 75081
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                P.O. Box 452017<br />
                Garland, TX 75044
              </p>
            </div>

            {/* Email */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900 mb-4">
                <Mail className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t(language, 'contact.email')}
              </h3>
              <a 
                href="mailto:mheeotcdallas@gmail.com"
                className="text-amber-600 dark:text-amber-400 hover:underline"
              >
                mheeotcdallas@gmail.com
              </a>
            </div>

            {/* Phone */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900 mb-4">
                <Phone className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t(language, 'contact.phone')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {language === 'en' ? 'Available via email' : 'ብኢመይል ርኸቡና'}
              </p>
            </div>
          </div>

          {/* Social Media */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8" style={{fontFamily: 'Playfair Display, serif'}}>
              {t(language, 'contact.followUs')}
            </h2>
            <div className="flex justify-center space-x-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-14 h-14 rounded-full ${
                      social.name === 'WhatsApp'
                        ? 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500'
                        : 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600'
                    } text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg`}
                    aria-label={social.name}
                  >
                    {social.name === 'WhatsApp' ? (
                      <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    ) : (
                      <Icon className="w-7 h-7" />
                    )}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Map */}
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3346.7887262557647!2d-96.66965!3d32.97668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c1f41f8dc6afd%3A0x7b50b8f7e5bf5b5c!2s1450%20E%20Campbell%20Rd%2C%20Richardson%2C%20TX%2075081!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Church Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
